import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { actions as subscriptionActions } from '../../../../actions/subscriptionActions'
import { actions as navigationActions } from '../../../../actions/navigationActions'
import AccountDetailsSection from './AccountDetailsSection'
import ConfigurationSection from './ConfigurationSection'
import WelcomeMessageSection from './WelcomeMessageSection'
import SupportTicketsSection from './SupportTicketsSection'
import GoalsSection from './GoalsSection'
import UserAccessSection from './UserAccessSection'
import * as types from '../../../../constants/actionTypes'
import Notification from '../../../Common/Notification'
import LoadingIndicator from '../../../Common/LoadingIndicator/LoadingIndicator'
import DocumentTitle from 'react-document-title'
import { defaultLogoColors } from '../../../../constants/constants'
import Button from '../../../Common/Button'
import { isValidCode } from '../../../../helpers/validation'
import './SubscriptionProfile.scss'
import { convertFromRaw, convertToRaw, EditorState } from 'draft-js'

export class SubscriptionProfile extends React.Component {
	constructor(props, context) {
		super(props, context)

		const emptyFAQs = [
			{
				id: 0,
				question: '',
				answer: '',
				isActive: true,
				ordinal: 0,
			},
		]

		const subscription = {
			id: 0,
			name: '',
			billingPlan: '',
			totalInflightTasks: 0,
			logoColor: defaultLogoColors.logoColor,

			isProjectIdRequiredForLocationTask: false,
			isStartDateActive: false,
			isTaskStepsActive: false,
			isTaskEffortActive: false,
			isTaskFinancialsActive: false,
			isSSOEnabled: false,
			isTaskDependenciesActive: false,
			isProjectWidgetActive: false,

			accessCode: '',
			approvedMessage: null,
			emailMessage: null,
			reportPageFooter: '',

			welcomeMessage: null,
			isWelcomeMessageActive: false,
			isWelcomeMessageConstant: false,

			faqs: emptyFAQs,
			defaultAgentUserId: '',

			isGoalWidgetActive: false,
			goalGroups: [
				{
					id: 0,
					subscriptionId: 0,
					name: '',
					isActive: true,
					ordinal: 0,
					goals: [],
				},
			],
		}

		this.state = {
			isLoading: false,
			subscription: subscription,

			isFormValid: true,
			isAccessCodeFormValid: true,
			isAccessCodeResponseValid: true,
			showApprovedMessageRequired: false,
			showUserInviteMessageRequired: false,
			showWelcomeMessageRequired: false,

			supportAgents: [],
			emptyFAQs: emptyFAQs,

			logoToUpload: null,
			imageBytes: '',
			imageUrl: '',

			welcomeMessageEditorState: null,
			approvedMessageEditorState: null,
			emailMessageEditorState: null,
		}
	}

	async componentDidMount() {
		try {
			if (this.props.subscriptionId) {
				this.setState({ isLoading: true })
				const response = await this.props.subscriptionActions.getSubscriptionProfile()
				if (response.type === types.GET_SUBSCRIPTION_PROFILE_SUCCESS) {
					// Ensure a blank FAQ exists if there are no FAQs on the Subscription
					let subscription = {
						...this.state.subscription,
						...response.payload.subscriptionProfile,
					}
					// convert from bizarre json serialization casing when all caps
					subscription.faqs = subscription.faQs.slice()
					if (!subscription.faqs || !subscription.faqs.length) {
						subscription.faqs = this.state.emptyFAQs
					}

					// v4 data workarounds for wysiwyg editors
					let welcomeMessageEditorState = EditorState.createEmpty()
					let approvedMessageEditorState = EditorState.createEmpty()
					let emailMessageEditorState = EditorState.createEmpty()

					if (subscription.welcomeMessage) {
						try {
							const welcomeMessageJson = JSON.parse(subscription.welcomeMessage)
							const welcomeMessageRaw = convertFromRaw(welcomeMessageJson)
							welcomeMessageEditorState = EditorState.createWithContent(welcomeMessageRaw)
						} catch (error) {
							// Ignore v4 HTML message
						}
					}

					if (subscription.approvedMessage) {
						try {
							const approvedMessageJson = JSON.parse(subscription.approvedMessage)
							const approvedMessageRaw = convertFromRaw(approvedMessageJson)
							approvedMessageEditorState = EditorState.createWithContent(approvedMessageRaw)
						} catch (error) {
							// Ignore v4 HTML message
						}
					}

					if (subscription.emailMessage) {
						try {
							const emailMessageJson = JSON.parse(subscription.emailMessage)
							const emailMessageRaw = convertFromRaw(emailMessageJson)
							emailMessageEditorState = EditorState.createWithContent(emailMessageRaw)
						} catch (error) {
							// Ignore v4 HTML message
						}
					}
					// End v4 data workarounds

					this.setState({
						subscription: subscription,
						imageBytes: response.payload.imageBytes,
						imageUrl: response.payload.imageUrl,
						supportAgents: response.payload.supportAgents,
						welcomeMessageEditorState: welcomeMessageEditorState,
						approvedMessageEditorState: approvedMessageEditorState,
						emailMessageEditorState: emailMessageEditorState,
					})
				} else if (response.type === types.GET_SUBSCRIPTION_PROFILE_FAILURE) {
					Notification('error', 'Error', response.payload.message)
				}
			}
		} catch (error) {
			Notification('error', 'Error', error)
		} finally {
			this.setState({ isLoading: false })
		}
	}

	onFormChange = (name, value) => {
		if (name === 'logoToUpload') {
			this.setState({
				...this.state,
				[name]: value,
			})
		} else {
			this.setState({
				...this.state,
				subscription: {
					...this.state.subscription,
					[name]: value,
				},
			})
		}
	}

	validateForm = () => {
		let isFormValid = true
		let isShowWelcomeMessageRequired = false
		let isShowApprovedMessageRequired = false
		let isShowUserInviteMessageRequired = false

		const accessCode = this.state.subscription.accessCode
		const isAccessCodeFormValid = accessCode && accessCode.trim().length > 0 && isValidCode(accessCode)
		if (!isAccessCodeFormValid) {
			isFormValid = false
		}

		if (this.state.subscription.isWelcomeMessageActive) {
			const welcomeMessage = this.state.subscription.welcomeMessage
			if (!welcomeMessage) {
				isFormValid = false
				isShowWelcomeMessageRequired = true
			} else {
				const welcomeMessageRaw = convertToRaw(this.state.welcomeMessageEditorState.getCurrentContent())
				if (welcomeMessageRaw.blocks[0].text === '') {
					isFormValid = false
					isShowWelcomeMessageRequired = true
				}
			}
		}

		if (!this.state.subscription.approvedMessage) {
			isShowApprovedMessageRequired = true
			isFormValid = false
		} else {
			const approvedMessageJson = JSON.parse(this.state.subscription.approvedMessage)
			if (approvedMessageJson.blocks[0].text === '') {
				isShowApprovedMessageRequired = true
				isFormValid = false
			}
		}

		if (!this.state.subscription.emailMessage) {
			isShowUserInviteMessageRequired = true
			isFormValid = false
		} else {
			const emailMessageJson = JSON.parse(this.state.subscription.emailMessage)
			if (emailMessageJson.blocks[0].text === '') {
				isShowUserInviteMessageRequired = true
				isFormValid = false
			}
		}

		this.setState({
			isFormValid: isFormValid,
			isAccessCodeFormValid: isAccessCodeFormValid,
			isAccessCodeResponseValid: true,
			showWelcomeMessageRequired: isShowWelcomeMessageRequired,
			showApprovedMessageRequired: isShowApprovedMessageRequired,
			showUserInviteMessageRequired: isShowUserInviteMessageRequired,
		})
		return isFormValid
	}

	onClickSave = async (event) => {
		event.preventDefault()
		event.stopPropagation()
		try {
			if (this.validateForm()) {
				this.setState({ isLoading: true })
				let subscription = this.state.subscription

				// remove all blank faqs and groups
				subscription.faqs = subscription.faqs.filter(
					(x) => x.question && x.question.trim().length > 0 && x.answer && x.answer.trim().length > 0
				)
				subscription.goalGroups = subscription.goalGroups.filter((x) => x.name && x.name.trim().length > 0)

				// Set the messages from the wysiwyg states
				const welcomeMessageJson = convertToRaw(this.state.welcomeMessageEditorState.getCurrentContent())
				const approvedMessageJson = convertToRaw(this.state.approvedMessageEditorState.getCurrentContent())
				const emailMessageJson = convertToRaw(this.state.emailMessageEditorState.getCurrentContent())
				subscription.welcomeMessage = JSON.stringify(welcomeMessageJson)
				subscription.approvedMessage = JSON.stringify(approvedMessageJson)
				subscription.emailMessage = JSON.stringify(emailMessageJson)

				const result = await this.props.subscriptionActions.saveSubscriptionProfile(subscription, this.state.logoToUpload)
				if (result.type === types.UPDATE_SUBSCRIPTION_PROFILE_SUCCESS) {
					Notification('success', 'Update Successful', 'Your subscription has been updated.')
					if (result.payload.imageBytes) {
						this.setState({
							imageBytes: result.payload.imageBytes,
						})
						this.props.navigationActions.setSubscriptionLogo(result.payload.imageBytes)
					}
				} else if (result.type === types.UPDATE_SUBSCRIPTION_PROFILE_FAILURE) {
					if (!result.payload.IsValidAccessCode) {
						this.setState({
							isAccessCodeResponseValid: result.payload.IsValidAccessCode,
						})
					}
					Notification('error', 'Error', result.payload.message)
				}
			}
		} catch (error) {
			Notification('error', 'Error', error)
		} finally {
			this.setState({ isLoading: false })
		}
	}

	onApprovedMessageChange = (editorState) => {
		const messageJson = convertToRaw(editorState.getCurrentContent())
		let isRequired = false
		if (messageJson.blocks[0].text === '') {
			isRequired = true
		}

		this.setState({
			showApprovedMessageRequired: isRequired,
			approvedMessageEditorState: editorState,
		})
	}

	onEmailMessageChange = (editorState) => {
		const messageJson = convertToRaw(editorState.getCurrentContent())
		let isRequired = false
		if (messageJson.blocks[0].text === '') {
			isRequired = true
		}

		this.setState({
			showUserInviteMessageRequired: isRequired,
			emailMessageEditorState: editorState,
		})
	}

	onWelcomeMessageChange = (editorState) => {
		const messageJson = convertToRaw(editorState.getCurrentContent())
		let isRequired = false
		if (this.state.subscription.isWelcomeMessageActive && messageJson.blocks[0].text === '') {
			isRequired = true
		}

		this.setState({
			showWelcomeMessageRequired: isRequired,
			welcomeMessageEditorState: editorState,
		})
	}

	render() {
		return (
			<div className='subscription-profile-wrapper'>
				<DocumentTitle title='Subscription Profile' />
				<div className='subscription-profile'>
					<h2 className='subscription-profile-header'>Profile Settings</h2>
					<LoadingIndicator isLoading={this.state.isLoading}>
						<AccountDetailsSection
							subscription={this.state.subscription}
							isFormValid={this.state.isFormValid}
							onFormChange={this.onFormChange}
							logoImage={this.state.imageBytes}
							imageUrl={this.state.imageUrl}
						/>
						<ConfigurationSection
							subscription={this.state.subscription}
							isFormValid={this.state.isFormValid}
							onFormChange={this.onFormChange}
						/>
						<UserAccessSection
							subscription={this.state.subscription}
							isFormValid={this.state.isFormValid}
							onFormChange={this.onFormChange}
							onEmailMessageChange={this.onEmailMessageChange}
							onApprovedMessageChange={this.onApprovedMessageChange}
							isAccessCodeFormValid={this.state.isAccessCodeFormValid}
							isAccessCodeResponseValid={this.state.isAccessCodeResponseValid}
							showApprovedMessageRequired={this.state.showApprovedMessageRequired}
							showUserInviteMessageRequired={this.state.showUserInviteMessageRequired}
							approvedMessageEditorState={this.state.approvedMessageEditorState}
							emailMessageEditorState={this.state.emailMessageEditorState}
						/>
						<WelcomeMessageSection
							subscription={this.state.subscription}
							isFormValid={this.state.isFormValid}
							onFormChange={this.onFormChange}
							onWelcomeMessageChange={this.onWelcomeMessageChange}
							showWelcomeMessageRequired={this.state.showWelcomeMessageRequired}
							editorState={this.state.welcomeMessageEditorState}
						/>
						<SupportTicketsSection
							subscription={this.state.subscription}
							isFormValid={this.state.isFormValid}
							onFormChange={this.onFormChange}
							supportAgents={this.state.supportAgents}
						/>
						<GoalsSection subscription={this.state.subscription} isFormValid={this.state.isFormValid} onFormChange={this.onFormChange} />
						<div className='save-button'>
							<Button type='submit' bootstrapClass='primary' text='Save' onClick={this.onClickSave} />
						</div>
					</LoadingIndicator>
				</div>
			</div>
		)
	}
}
SubscriptionProfile.propTypes = {
	subscriptionId: PropTypes.number.isRequired,
	subscriptionActions: PropTypes.object.isRequired,
	navigationActions: PropTypes.object.isRequired,
	onClose: PropTypes.func.isRequired,
}

function mapDispatchToProps(dispatch) {
	return {
		subscriptionActions: bindActionCreators(subscriptionActions, dispatch),
		navigationActions: bindActionCreators(navigationActions, dispatch),
	}
}

export default connect(null, mapDispatchToProps)(SubscriptionProfile)
